import { z } from 'zod';

/**
 * Number inputs start empty ('') so typing into a fresh field cannot produce a
 * leading zero like "05". Both '' and NaN must arrive as undefined so the
 * required message shows instead of a type error.
 */
const requiredNumber = (schema: z.ZodType<number>) =>
  z.preprocess(val => (val === '' || val === null || Number.isNaN(val) ? undefined : val), schema);

const kpiItemSchema = z.object({
  org_key_activity_id: z.number().min(1, 'Key Activities wajib dipilih'),
  org_job_description_id: z.number().min(1, 'Uraian tugas wajib dipilih'),
  weight: requiredNumber(
    z
      .number({ required_error: 'Bobot harus lebih dari 0', invalid_type_error: 'Bobot harus lebih dari 0' })
      .min(1, 'Bobot harus lebih dari 0')
      .max(100, 'Bobot maksimal 100'),
  ),
  target: requiredNumber(
    z
      .number({ required_error: 'Target harus lebih dari 0', invalid_type_error: 'Target harus lebih dari 0' })
      .min(1, 'Target harus lebih dari 0'),
  ),
  measurement: z.string().optional(),
});

export const kpiTemplateFormSchema = z
  .object({
    template_name: z.string().min(1, 'Nama template wajib diisi'),
    org_chart_node_id: z.number().min(1, 'Peran wajib dipilih'),
    start_date: z.string().min(1, 'Tanggal mulai wajib diisi'),
    end_date: z.string().min(1, 'Tanggal selesai wajib diisi'),
    is_published: z.boolean().default(false),
    items: z.array(kpiItemSchema).min(1, 'Minimal 1 item KPI'),
  })
  .refine(
    data => {
      if (!data.start_date || !data.end_date) return true;
      const startYear = data.start_date.split('-')[0];
      const endYear = data.end_date.split('-')[0];
      return startYear === endYear;
    },
    {
      message: 'Tahun pada tanggal mulai dan selesai harus sama',
      path: ['end_date'],
    },
  )
  .refine(
    data => {
      if (!data.start_date || !data.end_date) return true;
      const start = new Date(data.start_date);
      const end = new Date(data.end_date);
      return start <= end;
    },
    {
      message: 'Tanggal yang Anda masukkan tidak valid. Pastikan tanggal mulai tidak melebihi tanggal selesai.',
      path: ['start_date'],
    },
  )
  .refine(
    data => {
      const ids = data.items.map(i => i.org_job_description_id).filter(id => id > 0);
      return ids.length === new Set(ids).size;
    },
    data => {
      const ids = data.items.map(i => i.org_job_description_id);
      const seen = new Set<number>();
      const dupIndex = ids.findIndex(id => {
        if (id > 0 && seen.has(id)) return true;
        seen.add(id);
        return false;
      });
      return {
        message: 'Uraian tugas tidak boleh sama dalam satu template KPI',
        path: ['items', dupIndex, 'org_job_description_id'],
      };
    },
  );

export type KpiTemplateFormValues = z.infer<typeof kpiTemplateFormSchema>;
export type KpiItemFormValues = z.infer<typeof kpiItemSchema>;

export const defaultKpiItem: KpiItemFormValues = {
  org_key_activity_id: 0,
  org_job_description_id: 0,
  weight: '' as unknown as number,
  target: '' as unknown as number,
  measurement: '',
};
